import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Reveal, RevealImage } from "./Reveal";
import Magnetic from "./Magnetic";
import { scrollToId } from "@/lib/scroll";

const IMAGES = [
  {
    src: "/assets/editorial-1.webp",
    alt: "Skinfill Bacio campaign — natural radiant beauty with the Bacio syringe",
  },
  {
    src: "/assets/editorial-2.webp",
    alt: "Skinfill Bacio application moment — vitamin B12 and hyaluronic acid",
  },
  {
    src: "/assets/editorial-3.webp",
    alt: "Skinfill Bacio box in signature pink campaign lighting",
  },
];

const MARQUEE_ITEMS = [
  "HYDRATE",
  "REVITALISE",
  "GLOW",
  "NOT A FILLER — A LIP BOOSTER",
  "LOVE YOUR LIPS. BOOST THEM.",
  "THE NEW ERA OF LIP BEAUTY",
  "AUSTRALIA + NEW ZEALAND",
];

const Editorial = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section ref={ref} data-testid="editorial-section" className="bg-[#1C1417] pt-24 sm:pt-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <Reveal>
          <p className="font-mono text-[11px] sm:text-xs uppercase tracking-[0.28em] text-rose-300">
            The Bacio experience
          </p>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="font-serif font-light text-[#FAF7F5] text-[12vw] sm:text-7xl lg:text-8xl tracking-tight leading-[0.98] mt-6 text-balance">
            YOUR LIPS.
            <br />
            <span className="italic text-[#E893AE]">ONLY BETTER.</span>
          </h2>
        </Reveal>
        <Reveal delay={0.16}>
          <p className="mt-7 max-w-md text-[#F4EBE6]/70 text-base sm:text-lg font-light leading-relaxed">
            Hydrated. Smooth. Naturally radiant.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {IMAGES.map((img, i) => (
            <motion.div key={img.src} style={i === 1 ? { y } : undefined} className={i === 1 ? "lg:mt-16" : ""}>
              <RevealImage
                delay={i * 0.12}
                className={`relative overflow-hidden rounded-[24px] group ${
                  i === 2 ? "col-span-2 lg:col-span-1" : ""
                } aspect-[3/4]`}
              >
                <img
                  src={img.src}
                  alt={img.alt}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-[1400ms] ease-out group-hover:scale-[1.06]"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#1C1417]/35 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
              </RevealImage>
            </motion.div>
          ))}
        </div>

        <Reveal delay={0.2}>
          <div className="mt-14 flex justify-center">
            <Magnetic>
              <button
                data-testid="editorial-cta-results"
                onClick={() => scrollToId("#before-after")}
                className="btn-lux btn-lux-ghost"
              >
                <span>View Results</span>
              </button>
            </Magnetic>
          </div>
        </Reveal>
      </div>

      <div className="mt-20 sm:mt-24 border-y border-white/10 py-5 overflow-hidden" data-testid="editorial-marquee">
        <div className="flex whitespace-nowrap animate-marquee w-max">
          {[...MARQUEE_ITEMS, ...MARQUEE_ITEMS].map((item, i) => (
            <span key={i} className="flex items-center">
              <span className="font-serif italic font-light text-[#F4EBE6]/55 text-lg sm:text-2xl px-6">{item}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-[#D84A75]/60" />
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Editorial;
